/**
 * Command-center endpoints on Skynet.Loans (DEV-4096).
 *
 * - GET /api/command-center/locks/exposure      — expiry bucket totals (today / 48h / 7d / float).
 * - GET /api/command-center/pipeline/funnel     — milestone conversion funnel (ordered, monotonic).
 * - GET /api/command-center/turn-times/trend    — App→CTC median-days sparkline by week.
 * - GET /api/command-center/turn-times/stages   — median days per pipeline stage
 *                                                 (App→Approval, Approval→CTC, CTC→Funding).
 * - GET /api/command-center/summary             — company-wide KPI snapshot (funded MTD, active
 *                                                 pipeline, pull-through, App→CTC, locks exp 7d).
 * - GET /api/command-center/volume/monthly      — month-by-month funded-units series for the bar
 *                                                 chart in CompanyVolumeZone.
 * - GET /api/command-center/pipeline/queue-depth — per-stage active counts + per-staff workload
 *                                                 (DEV-4175).
 * - GET /api/command-center/funded/epo-watch    — funded loans inside the early-payoff window,
 *                                                 bucketed by days-since-funding (DEV-4176).
 * - GET /api/command-center/disclosures/timing  — outstanding eSign / intent-to-proceed with
 *                                                 days-since-disclosures-sent (DEV-4174).
 *
 * All endpoints are company-scoped by the authenticated user's JWT; no companyId param needed.
 */
export class CommandCenterLoansApi {
  #http;

  constructor(http) {
    this.#http = http;
  }

  async getLockExposure({ config = {} } = {}) {
    return this.#http.get('/api/command-center/locks/exposure', config);
  }

  async getPipelineFunnel({ config = {} } = {}) {
    return this.#http.get('/api/command-center/pipeline/funnel', config);
  }

  async getTurnTimeTrend({ weeks = 8, definitionId = null, config = {} } = {}) {
    const params = definitionId ? { weeks, definitionId } : { weeks };

    return this.#http.get('/api/command-center/turn-times/trend', { ...config, params });
  }

  async getTurnTimeStages({ windowDays = 90, config = {} } = {}) {
    return this.#http.get('/api/command-center/turn-times/stages', {
      ...config,
      params: { windowDays },
    });
  }

  async getSummary({ config = {} } = {}) {
    return this.#http.get('/api/command-center/summary', config);
  }

  async getMonthlyVolume({ months = 6, config = {} } = {}) {
    return this.#http.get('/api/command-center/volume/monthly', { ...config, params: { months } });
  }

  async getQueueDepth({ config = {} } = {}) {
    return this.#http.get('/api/command-center/pipeline/queue-depth', config);
  }

  async getEpoWatch({ windowMonths = 6, config = {} } = {}) {
    return this.#http.get('/api/command-center/funded/epo-watch', {
      ...config,
      params: { windowMonths },
    });
  }

  async getDisclosureTiming({ config = {} } = {}) {
    return this.#http.get('/api/command-center/disclosures/timing', config);
  }

  async getClosingThisWeek({ config = {} } = {}) {
    return this.#http.get('/api/command-center/closing/this-week', config);
  }
}
